// src/store/slices/dietarySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { supabase } from '../../utils/supabaseClient';
import { DietaryPreference } from '../../types/dietary';

export interface DietaryState {
    preferences: DietaryPreference[];
    isLoading: boolean;
    isCreating: boolean;
    isUpdating: boolean;
    isDeleting: boolean;
    error: string | null;
}

const initialState: DietaryState = {
    preferences: [],
    isLoading: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
    error: null,
};

// Async thunks
export const fetchDietaryPreferences = createAsyncThunk(
    'dietary/fetchPreferences',
    async (branchId: string) => {
        const { data, error } = await supabase.rpc('get_all_preferences' as any, {
            branch_id: branchId
        });
        if (error) throw error;

        let preferencesArray = data;
        if (data && typeof data === 'object' && !Array.isArray(data)) {
            preferencesArray = data.preferences || data.data || data.items || [];
        }

        const mappedData: DietaryPreference[] = (Array.isArray(preferencesArray) ? preferencesArray : []).map((item: any) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            ar: item.ar || { name: "", description: "" },
            branchId: branchId,
        }));

        return mappedData;
    }
);

export const createDietaryPreference = createAsyncThunk(
    'dietary/createPreference',
    async ({ branchId, data }: { branchId: string; data: Pick<DietaryPreference, 'name' | 'description' | 'ar'> }) => {
        // Insert: no id, no delete_id - uses upsert_dietary_preference
        const payload = {
            branch_id: branchId,
            name: data.name,
            description: data.description || null,
            ar: data.ar || { name: '', description: '' },
        };

        const { data: result, error } = await supabase.rpc('upsert_dietary_preference' as any, { payload });

        if (error) throw error;
        if (!result || result.success === false) {
            throw new Error(result?.message || 'Failed to add dietary preference');
        }

        // Return the created preference (normalize the response)
        return {
            id: result.data,
            name: data.name,
            description: data.description || '',
            ar: data.ar,
            branchId: branchId,
        } as DietaryPreference;
    }
);

export const updateDietaryPreference = createAsyncThunk(
    'dietary/updatePreference',
    async ({ id, data }: { id: string; data: Pick<DietaryPreference, 'name' | 'description' | 'ar'> }) => {
        // Update: include id, no delete_id - uses upsert_dietary_preference
        const payload = {
            id: id,
            name: data.name,
            description: data.description || null,
            ar: data.ar || { name: '', description: '' },
        };

        const { data: result, error } = await supabase.rpc('upsert_dietary_preference' as any, { payload });

        if (error) throw error;
        if (!result || result.success === false) {
            throw new Error(result?.message || 'Failed to update dietary preference');
        }

        return { id, ...data } as DietaryPreference;
    }
);

export const deleteDietaryPreference = createAsyncThunk(
    'dietary/deletePreference',
    async (id: string) => {
        // Delete: include delete_id - uses upsert_dietary_preference
        const payload = {
            delete_id: id
        };

        const { data: result, error } = await supabase.rpc('upsert_dietary_preference' as any, { payload });

        if (error) throw error;
        if (!result || result.success === false) {
            throw new Error(result?.message || 'Failed to delete dietary preference');
        }

        return id;
    }
);

const dietarySlice = createSlice({
    name: 'dietary',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        clearPreferences: (state) => {
            state.preferences = [];
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch preferences
            .addCase(fetchDietaryPreferences.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchDietaryPreferences.fulfilled, (state, action) => {
                state.isLoading = false;
                state.preferences = action.payload;
                state.error = null;
            })
            .addCase(fetchDietaryPreferences.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.error.message || 'Failed to fetch dietary preferences';
            })
            // Create preference
            .addCase(createDietaryPreference.pending, (state) => {
                state.isCreating = true;
                state.error = null;
            })
            .addCase(createDietaryPreference.fulfilled, (state, action) => {
                state.isCreating = false;
                state.preferences.push(action.payload);
                state.error = null;
            })
            .addCase(createDietaryPreference.rejected, (state, action) => {
                state.isCreating = false;
                state.error = action.error.message || 'Failed to create dietary preference';
            })
            // Update preference
            .addCase(updateDietaryPreference.pending, (state) => {
                state.isUpdating = true;
                state.error = null;
            })
            .addCase(updateDietaryPreference.fulfilled, (state, action) => {
                state.isUpdating = false;
                const index = state.preferences.findIndex(p => p.id === action.payload.id);
                if (index !== -1) {
                    state.preferences[index] = { ...state.preferences[index], ...action.payload };
                }
                state.error = null;
            })
            .addCase(updateDietaryPreference.rejected, (state, action) => {
                state.isUpdating = false;
                state.error = action.error.message || 'Failed to update dietary preference';
            })
            // Delete preference
            .addCase(deleteDietaryPreference.pending, (state) => {
                state.isDeleting = true;
                state.error = null;
            })
            .addCase(deleteDietaryPreference.fulfilled, (state, action) => {
                state.isDeleting = false;
                state.preferences = state.preferences.filter(p => p.id !== action.payload);
                state.error = null;
            })
            .addCase(deleteDietaryPreference.rejected, (state, action) => {
                state.isDeleting = false;
                state.error = action.error.message || 'Failed to delete dietary preference';
            });
    },
});

export const { clearError, clearPreferences } = dietarySlice.actions;
export default dietarySlice.reducer;
